import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';

import { API_BASE } from '../../config/runtime';

const getTimeAgo = (createdAt) => {
  if (!createdAt) return 'Just now';
  
  const notificationTime = new Date(createdAt).getTime();
  const currentTime = Date.now();
  const diff = Math.floor((currentTime - notificationTime) / 1000);
  
  if (diff < 60) return 'Just now';
  else if (diff < 3600) { const m = Math.floor(diff / 60); return `${m} minute${m > 1 ? 's' : ''} ago`; }
  else if (diff < 86400) { const h = Math.floor(diff / 3600); return `${h} hour${h > 1 ? 's' : ''} ago`; }
  else if (diff < 604800) { const d = Math.floor(diff / 86400); return `${d} day${d > 1 ? 's' : ''} ago`; }
  else return new Date(createdAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

const normalizeNotificationMessage = (message) => {
  if (typeof message !== 'string') return message;
  return message.replace(/booking request\s*\(#\d+\)/i, 'booking request');
};

const BellIcon = () => (
  <svg className="h-6 w-6" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
    <path d="M12 3a5 5 0 0 0-5 5v2.4c0 .9-.3 1.8-.8 2.6L4.8 15a1 1 0 0 0 .8 1.6h12.8a1 1 0 0 0 .8-1.6l-1.4-2a4.9 4.9 0 0 1-.8-2.6V8a5 5 0 0 0-5-5z" />
    <path d="M9.5 19a2.5 2.5 0 0 0 5 0" />
  </svg>
);

const NotificationBell = ({ isScrolled = false, forceBrown = false }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [timeKey, setTimeKey] = useState(0);
  const dropdownRef = useRef(null);
  const [menuOpenId, setMenuOpenId] = useState(null);
  const closeMenu = () => setMenuOpenId(null);

  const getStoredUser = () => {
    try {
      const rawUser = localStorage.getItem('user');
      return rawUser ? JSON.parse(rawUser) : null;
    } catch { return null; }
  };

  const user = getStoredUser();
  const userId = user?.user_id;
  const navigate = useNavigate();

  const textColor = forceBrown || isScrolled ? 'text-[#6f4a42]' : 'text-white';
  const buttonTone = (forceBrown || isScrolled)
    ? 'bg-transparent border-[#eaded5]'
    : 'bg-white/10 border-white/30 shadow-[0_12px_30px_rgba(20,14,10,0.25)]';

  const fetchNotifications = useCallback(async () => {
    if (!userId) { setNotifications([]); setUnreadCount(0); setLoading(false); return; }
    try {
      const response = await fetch(`${API_BASE}/get_notifications.php?user_id=${userId}`, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
      });
      const data = await response.json();
      if (data.success) { setNotifications(data.notifications); setUnreadCount(data.unread_count); }
    } catch (error) {
      console.error('Error fetching notifications:', error);
      setNotifications([]); setUnreadCount(0);
    } finally { setLoading(false); }
  }, [userId]);

  useEffect(() => { if (!userId) return; fetchNotifications(); }, [userId, fetchNotifications]);

  useEffect(() => {
    if (!userId) return;
    const interval = setInterval(() => { setTimeKey(prev => prev + 1); fetchNotifications(); }, 30000);
    return () => clearInterval(interval);
  }, [userId, fetchNotifications]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false); closeMenu();
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const markAsRead = async (id) => {
    if (!userId) return;
    try {
      const response = await fetch(`${API_BASE}/mark_notification_read.php`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ notification_id: id, user_id: userId }),
      });
      const data = await response.json();
      if (data.success) {
        setNotifications(notifications.map(n => n.notification_id === id ? { ...n, is_read: true } : n));
        setUnreadCount(prev => Math.max(0, prev - 1));
      }
    } catch (error) { console.error('Error marking notification as read:', error); }
  };

  const getNotificationTarget = (notification) => {
    if (notification.type === 'promo') return '/treatments';
    if (notification.booking_status === 'Pending') return `/purchased?highlight=${notification.booking_id || notification.notification_id}`;
    if (notification.booking_id) return `/history?highlight=${notification.booking_id}`;
    return '/history';
  };

  const handleNotificationClick = (notification) => {
    markAsRead(notification.notification_id);
    const target = getNotificationTarget(notification);
    if (target) { setIsOpen(false); closeMenu(); navigate(target); }
  };

  const toggleMenu = (id) => setMenuOpenId((prev) => (prev === id ? null : id));

  const markAsUnread = async (id) => {
    if (!userId) return;
    try {
      const response = await fetch(`${API_BASE}/mark_notification_unread.php`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ notification_id: id, user_id: userId }),
      });
      const data = await response.json();
      if (data.success) {
        setNotifications(notifications.map(n => n.notification_id === id ? { ...n, is_read: false } : n));
        const wasRead = notifications.find(n => n.notification_id === id)?.is_read === 1;
        if (wasRead) setUnreadCount(prev => prev + 1);
      }
    } catch (error) { console.error('Error marking notification as unread:', error); }
    finally { closeMenu(); }
  };

  const deleteNotification = async (id) => {
    if (!userId) return;
    try {
      const response = await fetch(`${API_BASE}/delete_notification.php`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ notification_id: id, user_id: userId }),
      });
      const data = await response.json();
      if (data.success) {
        const wasUnread = notifications.find(n => n.notification_id === id)?.is_read === 0;
        setNotifications(prev => prev.filter(n => n.notification_id !== id));
        if (wasUnread) setUnreadCount(prev => Math.max(0, prev - 1));
      }
    } catch (error) { console.error('Error deleting notification:', error); }
    finally { closeMenu(); }
  };

  const markAllAsRead = async () => {
    if (!userId) return;
    try {
      const response = await fetch(`${API_BASE}/mark_notification_read.php`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ mark_all: true, user_id: userId }),
      });
      const data = await response.json();
      if (data.success) { setNotifications(notifications.map(n => ({ ...n, is_read: true }))); setUnreadCount(0); }
    } catch (error) { console.error('Error marking all as read:', error); }
  };

  const getTypeColor = (type) => {
    switch (type) {
      case 'success': return 'bg-green-500';
      case 'info': return 'bg-blue-500';
      case 'warning': return 'bg-yellow-500';
      case 'promo': return 'bg-[#B89F80]';
      default: return 'bg-gray-500';
    }
  };

  return (
    <div className="relative" ref={dropdownRef}>
      {/* Bell Button */}
      <button
        onClick={() => {
          const willOpen = !isOpen;
          setIsOpen(willOpen);
          if (willOpen) fetchNotifications();
          else closeMenu();
        }}
        className={`relative flex h-10 w-10 items-center justify-center rounded-full border transition-all duration-200 ${textColor} ${buttonTone}`}
        aria-label="Notifications"
      >
        <BellIcon />
        {unreadCount > 0 && (
          <span className="absolute -top-0.5 -right-0.5 flex h-5 min-w-[20px] items-center justify-center rounded-full bg-[#b07b60] px-1 text-[10px] font-semibold text-white ring-2 ring-white/80">
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        )}
      </button>

      {/* Dropdown
          - On mobile: fixed full-width panel anchored near top (below bell area)
          - On sm+: absolute dropdown, w-96, right-aligned
      */}
      {isOpen && (
        <>
          {/* Mobile: full-width fixed overlay panel */}
          <div className="fixed inset-x-3 top-16 z-50 sm:hidden rounded-2xl border border-[#efe4db] bg-white/95 shadow-[0_24px_60px_rgba(33,24,18,0.18)] ring-1 ring-black/5 backdrop-blur-xl overflow-hidden">
            <DropdownContent
              loading={loading}
              notifications={notifications}
              unreadCount={unreadCount}
              timeKey={timeKey}
              menuOpenId={menuOpenId}
              markAllAsRead={markAllAsRead}
              handleNotificationClick={handleNotificationClick}
              toggleMenu={toggleMenu}
              markAsUnread={markAsUnread}
              deleteNotification={deleteNotification}
              getTypeColor={getTypeColor}
            />
          </div>

          {/* Desktop: absolute dropdown */}
          <div className="hidden sm:block absolute right-0 mt-3 w-96 rounded-2xl border border-[#efe4db] bg-white/95 shadow-[0_24px_60px_rgba(33,24,18,0.18)] ring-1 ring-black/5 backdrop-blur-xl z-50 overflow-hidden">
            <DropdownContent
              loading={loading}
              notifications={notifications}
              unreadCount={unreadCount}
              timeKey={timeKey}
              menuOpenId={menuOpenId}
              markAllAsRead={markAllAsRead}
              handleNotificationClick={handleNotificationClick}
              toggleMenu={toggleMenu}
              markAsUnread={markAsUnread}
              deleteNotification={deleteNotification}
              getTypeColor={getTypeColor}
            />
          </div>
        </>
      )}
    </div>
  );
};

// Extracted dropdown content to avoid duplication between mobile/desktop
const DropdownContent = ({
  loading, notifications, unreadCount, timeKey, menuOpenId,
  markAllAsRead, handleNotificationClick, toggleMenu,
  markAsUnread, deleteNotification, getTypeColor,
}) => (
  <>
    {/* Header */}
    <div className="flex items-center justify-between border-b border-[#f1e7df] px-4 sm:px-5 py-3 sm:py-4 bg-[#fcf8f5]">
      <div className="flex items-center gap-2 sm:gap-3">
        <p className="text-[10px] font-semibold uppercase tracking-[0.28em] text-[#b07b60]">Inbox</p>
        <span className="text-[10px] text-[#c8b9af]">/</span>
        <h3 className="text-sm font-semibold text-[#2f2622]">Notifications</h3>
      </div>
      {unreadCount > 0 && (
        <button
          onClick={markAllAsRead}
          className="rounded-full border border-[#eaded5] bg-white px-2.5 sm:px-3 py-1 text-[11px] font-semibold text-[#8a6b5a] transition hover:bg-[#f7efe9] whitespace-nowrap"
        >
          Mark all as read
        </button>
      )}
    </div>

    {/* Notification List */}
    <div className="max-h-[60vh] sm:max-h-80 overflow-y-auto">
      {loading ? (
        <div className="px-5 py-10 text-center text-sm text-[#8a7a71]">Loading...</div>
      ) : notifications.length === 0 ? (
        <div className="px-5 py-10 text-center text-sm text-[#8a7a71]">No notifications yet</div>
      ) : (
        notifications.map((notification) => (
          <div
            key={notification.notification_id}
            onClick={() => handleNotificationClick(notification)}
            className={`cursor-pointer border-b border-[#f5ebe4] px-4 sm:px-5 py-3 sm:py-4 transition-colors hover:bg-[#fcf7f3] ${
              !notification.is_read ? 'bg-[#fbf4ef]' : ''
            }`}
          >
            <div className="flex items-start gap-3">
              <div className={`mt-1.5 h-2.5 w-2.5 flex-shrink-0 rounded-full ${getTypeColor(notification.type)} ${!notification.is_read ? 'ring-2 ring-[#e9d9cf]' : ''}`} />
              <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between gap-2">
                  <div className="flex items-center gap-2 min-w-0">
                    <p className={`text-sm font-semibold break-words ${!notification.is_read ? 'text-[#2f2622]' : 'text-[#5a4b44]'}`}>
                      {notification.title?.replace(/^#\d+\s*•?\s*/, '') || notification.title}
                    </p>
                    {!notification.is_read && (
                      <span className="h-2 w-2 flex-shrink-0 rounded-full bg-[#b07b60]" />
                    )}
                  </div>
                  <div className="relative flex-shrink-0">
                    <button
                      type="button"
                      onClick={(e) => { e.stopPropagation(); toggleMenu(notification.notification_id); }}
                      className="rounded-full p-1 text-[#9b8a80] hover:bg-[#f3e8df] hover:text-[#6f4a42]"
                      aria-label="Open notification menu"
                    >
                      <svg className="h-4 w-4" viewBox="0 0 24 24" fill="currentColor">
                        <circle cx="12" cy="6" r="1.5" />
                        <circle cx="12" cy="12" r="1.5" />
                        <circle cx="12" cy="18" r="1.5" />
                      </svg>
                    </button>
                    {menuOpenId === notification.notification_id && (
                      <div className="absolute right-0 top-6 z-20 w-44 rounded-xl border border-[#efe4db] bg-white shadow-lg">
                        <button
                          onClick={(e) => { e.stopPropagation(); markAsUnread(notification.notification_id); }}
                          className="block w-full px-4 py-2 text-left text-xs text-[#6f4a42] hover:bg-[#fbf3ee]"
                        >
                          Mark as unread
                        </button>
                        <button
                          onClick={(e) => { e.stopPropagation(); deleteNotification(notification.notification_id); }}
                          className="block w-full px-4 py-2 text-left text-xs text-rose-600 hover:bg-rose-50"
                        >
                          Delete
                        </button>
                      </div>
                    )}
                  </div>
                </div>
                <p className="mt-1 text-xs text-[#6f5f57] whitespace-normal break-words">
                  {normalizeNotificationMessage(notification.message)}
                </p>
                <div className="mt-1 flex items-center gap-2 flex-wrap">
                  <p className="text-xs text-[#9b8a80]" key={timeKey}>{getTimeAgo(notification.created_at)}</p>
                  <span className="text-xs text-[#c8b9af]">-</span>
                  <p className="text-xs text-[#9b8a80]">
                    {new Date(notification.created_at).toLocaleDateString('en-US', {
                      month: 'short', day: 'numeric', year: 'numeric',
                      hour: 'numeric', minute: '2-digit', hour12: true,
                    })}
                  </p>
                </div>
              </div>
            </div>
          </div>
        ))
      )}
    </div>
  </>
);

export default NotificationBell;
